// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Game Time Manager
 * 
 * Manages game time progression and triggers time-based events
 * - Resource consumption
 * - Crew morale changes
 * - Mission time tracking
 */

import ResourceManager from './ResourceManager';
import CrewManager from './CrewManager';
import CrewActivityGenerator from './CrewActivityGenerator';
import CrewConversationManager from './CrewConversationManager';
import EventBus, { EVENT_TYPES } from '../system/EventBus';
import { checkBackstoryReveals } from './CrewBackstoryIntegration';
import STORAGE_KEYS from '../system/STORAGE_KEYS';

class GameTimeManager {
  constructor() {
    // Game time tracking
    this.lastUpdateTime = Date.now();
    this.gameTimeElapsed = 0; // Total game time in seconds
    this.realTimeElapsed = 0; // Total real time in seconds
    
    // Time acceleration (1.0 = real-time, 60.0 = 1 minute per second)
    this.timeScale = 60.0; // Default: 1 real second = 1 game minute
    
    // Update intervals
    this.updateInterval = null;
    this.updateFrequency = 1000; // Update every 1 second (real time)
    
    
    // Day tracking for crew activities
    this.currentDay = 0;
    this.lastDayCheck = 0;
    // Consumption tracking
    this.lastResourceUpdate = Date.now();
    this.resourceUpdateInterval = 60000; // Update resources every 60 seconds (real time) = 1 hour game time
    
    // Load from localStorage
    this.loadFromStorage();
    
  }

  /**
   * Start the game time loop
   */
  start() {
    if (this.updateInterval) {
      console.warn('[GameTimeManager] Already running');
      return;
    }

    this.lastUpdateTime = Date.now();
    this.lastResourceUpdate = Date.now();
    
    this.updateInterval = setInterval(() => {
      this.update();
    }, this.updateFrequency);
    
  }

  /**
   * Stop the game time loop
   */
  stop() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * Main update loop
   */
  update() {
    const now = Date.now();
    const deltaRealTime = (now - this.lastUpdateTime) / 1000; // seconds
    const deltaGameTime = deltaRealTime * this.timeScale; // scaled game time
    
    this.lastUpdateTime = now;
    this.realTimeElapsed += deltaRealTime;
    this.gameTimeElapsed += deltaGameTime;
    
    // Update resources every minute (real time)
    if (now - this.lastResourceUpdate >= this.resourceUpdateInterval) {
      this.updateResources(deltaGameTime);
      this.lastResourceUpdate = now;
    }
    
    
    // Update crew training progress
    CrewManager.updateTrainingProgress();
    
    // Update crew conversations
    CrewConversationManager.update();
    
    // Check for backstory reveals based on relationships
    const backstoryReveals = checkBackstoryReveals();
    if (backstoryReveals.length > 0) {
    }
    
    // Check for day changes and process crew activities
    const currentDay = Math.floor(this.gameTimeElapsed / 86400); // 86400 seconds per day
    if (currentDay > this.currentDay) {
      this.processDayChange(currentDay);
      this.currentDay = currentDay;
    }
    
    // Save periodically
    if (Math.floor(this.realTimeElapsed) % 30 === 0) {
      this.saveToStorage();
    }
  }

  /**
   * Update resource consumption
   */
  updateResources(deltaGameTime) {
    const crewCount = CrewManager.getAllCrew().length;
    
    if (crewCount === 0) {
      return; // No crew, no consumption
    }

    // Convert game time to days (86400 seconds per day)
    const daysElapsed = deltaGameTime / 86400; // eslint-disable-line no-unused-vars
    
    // Get warnings before consumption
    const warnings = ResourceManager.updateResources(deltaGameTime, crewCount);
    
    // Check for critical shortages and apply consequences
    this.handleResourceShortages(warnings);
    
    // Log consumption
    if (warnings.length > 0) {
    }
  }

  /**
   * Handle resource shortage consequences
   */
  handleResourceShortages(warnings) {
    warnings.forEach(warning => {
      if (warning.type === 'critical' && warning.daysRemaining === 0) {
        // Resource depleted - apply consequences
      // eslint-disable-next-line default-case
        switch (warning.resource) {
          case 'food':
          case 'water':
          case 'oxygen':
            // Critical life support failure - crew casualties
            const casualties = Math.floor(Math.random() * 2) + 1; // 1-2 casualties
            const result = CrewManager.killCrew(casualties, `${warning.resource} depletion`);
            
            // Dispatch alert event
            EventBus.emit(EVENT_TYPES.GAME_ALERT, {
              message: `${warning.resource.toUpperCase()} DEPLETED - ${result.message}`,
              severity: 'critical',
              duration: 10000
            });
            
            // Major morale hit
            CrewManager.adjustMorale(-15);
            break;
            
          case 'medical':
            // Can't heal injuries - morale penalty
            CrewManager.adjustMorale(-5);
            EventBus.emit(EVENT_TYPES.GAME_ALERT, {
              message: 'MEDICAL SUPPLIES DEPLETED - Unable to treat injuries. Crew morale suffering.',
              severity: 'warning',
              duration: 8000
            });
            break;
            
          case 'parts':
            // Can't repair ship - no immediate consequence but noted
            EventBus.emit(EVENT_TYPES.GAME_ALERT, {
              message: 'SPARE PARTS DEPLETED - Unable to perform repairs. Ship systems at risk.',
              severity: 'warning',
              duration: 8000
            });
            break;
        }
      } else if (warning.type === 'critical') {
        // Low but not empty - warning only
        EventBus.emit(EVENT_TYPES.GAME_ALERT, {
          message: `${warning.message} - Resupply immediately at nearest station or planet.`,
          severity: 'warning',
          duration: 6000
        });
      }
    });
  }
  
  /**
   * Process day change and generate crew activities
   */
  processDayChange(newDay) {
    
    // Get current location (simplified - you may need to get this from NavigationManager)
    // const currentLocation = 'space'; // reserved for future use
    
    // Process daily crew activities
    try {
      CrewActivityGenerator.processDailyActivities();
      
      // Emit day change event for UI updates
      EventBus.emit(EVENT_TYPES.GAME_EVENT, {
        type: 'day_change',
        message: `Ship's Day ${newDay} begins`,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('[GameTimeManager] Error processing day change:', error);
    }
  }

  /**
   * Get current game time
   */
  getGameTime() {
    return this.gameTimeElapsed;
  }

  /**
   * Get time scale
   */
  getTimeScale() {
    return this.timeScale;
  }

  /**
   * Set time scale
   */
  setTimeScale(scale) {
    this.timeScale = Math.max(1.0, Math.min(3600.0, scale)); // Clamp between 1x and 3600x
  }

  /**
   * Reset game time
   */
  reset() {
    this.gameTimeElapsed = 0;
    this.realTimeElapsed = 0;
    this.lastUpdateTime = Date.now();
    this.lastResourceUpdate = Date.now();
    this.saveToStorage();
  }

  /**
   * Save to localStorage
   */
  saveToStorage() {
    try {
      const data = {
        gameTimeElapsed: this.gameTimeElapsed,
        realTimeElapsed: this.realTimeElapsed,
        timeScale: this.timeScale,
        currentDay: this.currentDay
      };
      localStorage.setItem(STORAGE_KEYS.GAME_TIME, JSON.stringify(data));
    } catch (error) {
      console.error('[GameTimeManager] Failed to save to localStorage:', error);
    }
  }

  /**
   * Load from localStorage
   */
  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.GAME_TIME);
      if (data) {
        const parsed = JSON.parse(data);
        this.gameTimeElapsed = parsed.gameTimeElapsed || 0;
        this.realTimeElapsed = parsed.realTimeElapsed || 0;
        this.timeScale = parsed.timeScale || 60.0;
        this.currentDay = parsed.currentDay || 0;
      }
    } catch (error) {
      console.error('[GameTimeManager] Failed to load from localStorage:', error);
    }
  }
}

// Singleton instance
const gameTimeManager = new GameTimeManager();
export default gameTimeManager;
